import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "system-prompt" }
const _hoisted_2 = { class: "system-prompt-content" }
const _hoisted_3 = {
  key: 0,
  class: "loading"
}
const _hoisted_4 = {
  key: 1,
  class: "error"
}
const _hoisted_5 = {
  key: 2,
  class: "prompt-editor"
}
const _hoisted_6 = {
  key: 0,
  class: "prompt-preview"
}
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = {
  key: 1,
  class: "prompt-edit"
}

import { useAuthStore } from "@/stores";
import { marked } from "marked";
import { computed, onMounted, ref } from "vue";

interface SystemPromptResponse {
  systemPrompt: string;
  error?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SystemPrompt',
  props: {
    assessmentId: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const authStore = useAuthStore();

// No longer emitting close event - handled by parent

const isLoading = ref(false);
const error = ref<string | null>(null);
const originalPrompt = ref("");
const editablePrompt = ref("");
const isEditing = ref(false);

// Helper functions for profile preferences
const getSavedPrompt = (): string | undefined => {
  return authStore.currentProfile?.state?.preferences?.systemPrompt;
};

const updatePromptPreference = (prompt: string | null) => {
  if (authStore.currentProfile) {
    const currentPreferences = authStore.currentProfile.state.preferences || {};
    const updatedProfile = {
      ...authStore.currentProfile,
      state: {
        ...authStore.currentProfile.state,
        preferences: {
          ...currentPreferences,
          systemPrompt: prompt,
        },
      },
    };
    authStore.setProfile(updatedProfile);
  }
};

const renderedPrompt = computed(() => {
  return marked(editablePrompt.value || "");
});

const fetchSystemPrompt = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) ||
      "http://localhost:3000/api";
    const assessmentId = props.assessmentId || "1"; // Fallback to "1" if no prop provided
    const response = await fetch(
      `${apiBaseUrl}/system-prompt/${assessmentId}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Järjestelmäkehoteen lataus epäonnistui");
    }

    const data = (await response.json()) as SystemPromptResponse;

    if (data.error) {
      throw new Error(data.error);
    }

    originalPrompt.value = data.systemPrompt;

    // Try to load saved version from profile state
    try {
      const savedPrompt = getSavedPrompt();
      editablePrompt.value = savedPrompt || originalPrompt.value;
    } catch (err) {
      console.warn(
        "Failed to load system prompt from profile state, using original:",
        err,
      );
      editablePrompt.value = originalPrompt.value;
    }
  } catch (err) {
    console.error("Error fetching system prompt:", err);
    error.value = err instanceof Error ? err.message : "Tuntematon virhe";
  } finally {
    isLoading.value = false;
  }
};

const startEditing = () => {
  isEditing.value = true;
};

const saveChanges = () => {
  // Save to profile state
  updatePromptPreference(editablePrompt.value);
  isEditing.value = false;
};

const cancelEditing = () => {
  // Revert to saved version
  try {
    const savedPrompt = getSavedPrompt();
    editablePrompt.value = savedPrompt || originalPrompt.value;
  } catch (err) {
    console.warn("Failed to load saved prompt, using original:", err);
    editablePrompt.value = originalPrompt.value;
  }
  isEditing.value = false;
};

const resetToDefault = () => {
  editablePrompt.value = originalPrompt.value;
  updatePromptPreference(null);
  isEditing.value = false;
};

// Close functionality now handled by parent component

// Expose method for external reset (when "Palauta oletustilaan" is clicked)
const resetSystemPrompt = () => {
  editablePrompt.value = originalPrompt.value;
  updatePromptPreference(null);
  isEditing.value = false;
};

// Expose method to get current prompt for API calls
const getCurrentPrompt = () => {
  return editablePrompt.value;
};

onMounted(() => {
  void fetchSystemPrompt();
});

// Expose methods for parent component
__expose({
  resetSystemPrompt,
  getCurrentPrompt,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, " Ladataan järjestelmäkehotetta... "))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("p", null, "Virhe järjestelmäkehoteen lataamisessa: " + _toDisplayString(error.value), 1),
              _createElementVNode("button", {
                onClick: fetchSystemPrompt,
                class: "retry-button"
              }, " Yritä uudelleen ")
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (!isEditing.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createElementVNode("div", { innerHTML: renderedPrompt.value }, null, 8, _hoisted_7),
                    _createElementVNode("button", {
                      onClick: startEditing,
                      class: "edit-button"
                    }, "Muokkaa")
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _withDirectives(_createElementVNode("textarea", {
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editablePrompt).value = $event)),
                      class: "prompt-textarea",
                      placeholder: "Kirjoita järjestelmäkehote tähän..."
                    }, null, 512), [
                      [_vModelText, editablePrompt.value]
                    ]),
                    _createElementVNode("div", { class: "edit-actions" }, [
                      _createElementVNode("button", {
                        onClick: saveChanges,
                        class: "save-button"
                      }, "Tallenna"),
                      _createElementVNode("button", {
                        onClick: cancelEditing,
                        class: "cancel-button"
                      }, " Peruuta "),
                      _createElementVNode("button", {
                        onClick: resetToDefault,
                        class: "reset-button"
                      }, " Palauta oletukseen ")
                    ])
                  ]))
            ]))
    ])
  ]))
}
}

})